import * as React from 'react';
import { instructionHooks } from '../network/endpoints.js';
import { Input, FormField } from '../../../components/ui/FormField';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { z } from 'zod';
import { useToast } from '../../../components/ui/Toast';

interface MarkSentModalProps {
	instructionId: string;
	isOpen: boolean;
	onClose: () => void;
}

const formSchema = z.object({
	bankRef: z.string().min(1, 'UTR/Bank Reference is required'),
	sentAt: z.string().optional(),
});

export function MarkSentModal({ instructionId, isOpen, onClose }: MarkSentModalProps) {
	const sendMutation = instructionHooks.useSend();
	const { toast } = useToast();

	const modal = useFormModal(
		{ defaultValues: { bankRef: '', sentAt: '' }, schema: formSchema },
		sendMutation,
		{
			onBeforeSubmit: (value) => ({
				id: instructionId,
				bankRef: value.bankRef,
				sentAt: value.sentAt || new Date().toISOString(),
			}),
			onSuccess: () => {
				toast({ type: 'success', title: 'Success', description: 'Bank transfer details recorded' });
				onClose();
			},
			onError: (err) => {
				toast({ type: 'error', title: 'Error', description: err?.message || 'Failed to record details' });
			},
		},
	);

	React.useEffect(() => {
		if (isOpen) {
			modal.form.reset();
		}
	}, [isOpen]);

	return (
		<FormModal open={isOpen} onClose={onClose} title="Record Bank Transfer Details" modal={modal} submitLabel="Record">
			<modal.form.Field
				name="bankRef"
				validators={{ onChange: ({ value }: { value: any }) => {
					const res = formSchema.shape.bankRef.safeParse(value);
					return res.success ? undefined : res.error.issues[0].message;
				}}}
				children={(field: any) => (
					<FormField
						label="Bank UTR / Reference"
						required
						error={modal.fieldError(field.state.meta)}
					>
						<Input
							type="text"
							value={field.state.value}
							onChange={(e) => field.handleChange(e.target.value)}
							onBlur={field.handleBlur}
							className="bg-surface-2"
							placeholder="e.g. UTR123456789"
							disabled={modal.isSubmitting}
						/>
					</FormField>
				)}
			/>

			<modal.form.Field
				name="sentAt"
				children={(field: any) => (
					<FormField
						label="Sent Date & Time"
						error={modal.fieldError(field.state.meta)}
						hint="Defaults to now if left empty"
					>
						<Input
							type="datetime-local"
							value={field.state.value}
							onChange={(e) => field.handleChange(e.target.value)}
							onBlur={field.handleBlur}
							className="bg-surface-2"
							disabled={modal.isSubmitting}
						/>
					</FormField>
				)}
			/>
		</FormModal>
	);
}
